import { Check } from "lucide-react";

interface SubmitReadinessProps {
  /** Declared flow inputs, or `undefined` when the manifest did not load and
   *  the page therefore does not know what the flow declares. */
  declaredInputCount: number | undefined;
  /** Declared input names with no artifact selected. Empty ⇒ all resolved. */
  missingInputs: readonly string[];
  /** Nodes the flow template declares, from the DAG endpoint. `undefined`
   *  while the DAG is loading or after it errored. */
  nodeCount: number | undefined;
}

/**
 * The pre-flight readiness line beside Submit — the 2026-08-21 canvas's
 * `Inputs resolve · 15 tiles compile` (flowstack#1282).
 *
 * The canvas's thesis for this page is *pre-submit confidence*: before you
 * commit to creating 15+ billed runs, the page should say what it already
 * knows about the submission you are about to make. The stub-mode banner
 * (#1174) answers "will this reach real compute". This answers the two
 * smaller questions above the Submit button: **is every input wired**, and
 * **how much is about to be compiled**.
 *
 * Two deliberate departures from the canvas's copy:
 *
 * 1. **"nodes", not "tiles".** The number is the flow template's node count —
 *    the same figure `FlowStructurePanel` prints as `N nodes · M edges` and
 *    the same one `POST /api/flows/submit` returns as `node_count`. The
 *    canvas wrote "tiles"; adopting it here would give one page two words
 *    for one quantity, which costs more than matching the artboard's noun.
 * 2. **"to compile", not "compile".** Nothing has been compiled at this
 *    point — `compile_flow` runs server-side inside `POST /api/flows/submit`,
 *    and there is no compile-only endpoint to ask (see the PR body: the
 *    canvas's `Dry run` has no server path behind it and was left out rather
 *    than faked). Stating `15 tiles compile` would assert a result we have
 *    not obtained, which is the same over-claim Rupert struck from the
 *    execution-mode notice in PR-1175 r0. We state the work, not its verdict.
 *
 * "Inputs resolve" IS asserted, because the client genuinely establishes it:
 * every declared input has a selection, and `ArtifactInputPicker` only offers
 * catalog artifacts whose `typed_object` equals the port's declared type — so
 * a full selection satisfies the name, presence and type checks
 * `_resolve_flow_inputs` performs server-side. It does NOT cover that route's
 * last check — a `scientific_status="failed"` upstream, which 400s without an
 * explicit `override_failed_upstream` and which the Flow submit surface does
 * not yet expose at all (only the Tile page renders `FailedUpstreamBanner`).
 * Hence the word is "resolve", not "accepted": the inputs are wired and typed,
 * which is a smaller claim than the submission being accepted.
 *
 * Renders nothing at all when it knows neither fact — an empty line is
 * better than a line that says nothing.
 */
export function SubmitReadiness({
  declaredInputCount,
  missingInputs,
  nodeCount,
}: SubmitReadinessProps): JSX.Element | null {
  const inputsResolve = declaredInputCount !== undefined && missingInputs.length === 0;

  const inputClause =
    declaredInputCount === undefined
      ? undefined
      : missingInputs.length > 0
        ? `Select an artifact for: ${missingInputs.join(", ")}`
        : declaredInputCount === 0
          ? "No inputs to resolve"
          : "Inputs resolve";

  const nodeClause =
    nodeCount === undefined
      ? undefined
      : `${nodeCount} node${nodeCount === 1 ? "" : "s"} to compile`;

  const clauses = [inputClause, nodeClause].filter((c): c is string => c !== undefined);
  if (clauses.length === 0) return null;

  return (
    <p
      // A status region rather than plain text: the line changes as inputs are
      // wired, and the change is the point. Polite, so it never interrupts.
      role="status"
      data-testid="submit-readiness"
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
    >
      {/* The tick marks "every input is wired", not "this submission will
          succeed" — so it is muted chrome, not a success colour. */}
      {inputsResolve && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      <span>{clauses.join(" · ")}</span>
    </p>
  );
}
